import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

interface PaymentSimulationProps {
    onPaymentSuccess: (tier: string) => void;
}

const PaymentSimulation: React.FC<PaymentSimulationProps> = ({ onPaymentSuccess }) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const sessionId = searchParams.get('session');
    const tier = searchParams.get('tier');
    const type = searchParams.get('type');
    const amount = searchParams.get('amount');
    const [status, setStatus] = useState<'initializing' | 'processing' | 'success'>('initializing');

    useEffect(() => {
        const timer = setTimeout(() => {
            setStatus('processing');
            const confirmTimer = setTimeout(async () => {
                try {
                    const response = await fetch('/api/subscription/confirm', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ sessionId, tier, type, amount })
                    });
                    if (response.ok) {
                        setStatus('success');
                        setTimeout(() => {
                            onPaymentSuccess(tier || 'current');
                            navigate('/');
                        }, 2000);
                    }
                } catch (e) {
                    console.error("Payment confirmation failed");
                }
            }, 3000);
            return () => clearTimeout(confirmTimer);
        }, 1500);
        return () => clearTimeout(timer);
    }, [sessionId, tier, type, amount, navigate, onPaymentSuccess]);

    return (
        <div className="min-h-screen bg-dk-900 flex items-center justify-center p-8 read-only-ui">
            <div className="glass-panel max-w-md w-full p-12 rounded-[2.5rem] text-center border-azure/20 shadow-2xl relative overflow-hidden">
                {status === 'initializing' && (
                    <div className="animate-fade-in space-y-6">
                        <div className="w-16 h-16 border-2 border-azure/30 border-t-azure rounded-full animate-spin mx-auto"></div>
                        <p className="text-xs font-black uppercase tracking-[0.3em] text-azure-400">Verifying Logic Transaction...</p>
                    </div>
                )}
                {status === 'processing' && (
                    <div className="animate-fade-in space-y-6">
                        <div className="relative w-20 h-20 mx-auto">
                            <div className="absolute inset-0 border-4 border-azure/20 rounded-full"></div>
                            <div className="absolute inset-0 border-4 border-transparent border-t-azure rounded-full animate-spin"></div>
                            <div className="absolute inset-4 bg-azure/10 rounded-full animate-pulse"></div>
                        </div>
                        <div className="space-y-2">
                            <h2 className="text-2xl font-black text-white italic uppercase tracking-tighter">
                                {type === 'topup' ? 'Refueling Logic' : 'Initializing Tier'}
                            </h2>
                            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest leading-relaxed">
                                {type === 'topup' 
                                    ? `Injecting ${amount} intelligence orchestrations...` 
                                    : `System is re-routing neural weights for ${tier} intelligence...`}
                            </p>
                        </div>
                    </div>
                )}
                {status === 'success' && (
                    <div className="animate-fade-in space-y-6">
                        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto border border-green-500/30">
                            <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                        </div>
                        <div className="space-y-2">
                            <h2 className="text-2xl font-black text-white italic uppercase tracking-tighter">Sync Successful</h2>
                            <p className="text-[10px] font-black text-green-400 uppercase tracking-[0.4em]">
                                {type === 'topup' ? `${amount} Credits Added` : `Intelligence Level: ${tier}`}
                            </p>
                        </div>
                    </div>
                )}
                <div className="absolute top-0 left-0 w-full h-1 bg-white/5 overflow-hidden">
                    <div className="h-full bg-azure shadow-[0_0_10px_#3b82f6] animate-progress"></div>
                </div>
            </div>
        </div>
    );
};

export default PaymentSimulation;
